import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import classes from "./AddPatientPage.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import ContactInfo from "./ContactInfo";
import MedicalInfo from "./MedicalInfo";
import {
  FaTimes,
  FaUserPlus,
  FaUser,
  FaPhone,
  FaHeartbeat,
  FaUsers,
  FaShieldAlt,
  FaFileAlt,
  FaUndo,
  FaArrowRight,
  FaArrowLeft,
  FaSave,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

const AddPatientPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [activeTab, setActiveTab] = useState("personal");
  const [generatedUHID, setGeneratedUHID] = useState("");
  const [isGeneratingUHID, setIsGeneratingUHID] = useState(false);
  const [uhidError, setUhidError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // Form state includes fields from all sections
  const [form, setForm] = useState({
    // Personal Fields
    firstName: "",
    middleName: "",
    lastName: "",
    uhid: "",
    dob: "",
    age: "",
    gender: "",
    bloodGroup: "",
    maritalStatus: "",
    nationality: "",
    religion: "",
    occupation: "",
    idType: "",
    idNumber: "",
    registrationDate: "",
    referredBy: "",
    // Address Fields
    address1: "",
    address2: "",
    country: "",
    state: "",
    city: "",
    postalCode: "",
    // Primary Physician
    department: "",
    doctor: "",
    consultationType: "",
    // Contact Information
    primaryPhone: "",
    alternativePhone: "",
    email: "",
    whatsappNumber: "",
    preferredContactMethod: "",
    preferredContactTime: "",
    preferredLanguage: "",
    smsNotifications: false,
    emailNotifications: false,
    appointmentReminders: false,
    sameAsResidential: false,
    contactAddress: "",
    // Medical Information Fields
    genotype: "",
    height: "",
    heightUnit: "cm",
    weight: "",
    weightUnit: "kg",
    noKnownConditions: false,
    noKnownAllergies: false,
    allergen: "",
    reaction: "",
    takingMedication: "No",
    medicationName: "",
    dosage: "",
    frequency: "",
    route: "",
    medicationStartDate: "",
    previousSurgery: "No",
    procedureName: "",
    surgeryDate: "",
    hospital: "",
    surgeon: "",
    surgeryNotes: "",
    pastMedicalHistory: "",
    familyMedicalHistory: "",
    additionalMedicalNotes: "",
    // Emergency Contact
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
    // Insurance
    insuranceId: "",
  });

  // Generate UHID on component mount
  useEffect(() => {
    generateUHID();
  }, []);

  // Function to generate UHID from API using GET method
  const generateUHID = async () => {
    setIsGeneratingUHID(true);
    setUhidError("");
    try {
      const companyId = localStorage.getItem("companyId") || "1";
      
      const response = await axios.get(
        `${BASE_URL}/api/patient/generate_number`,
        {
          params: {
            company_id: parseInt(companyId),
          },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("UHID Response:", response.data);

      let uhid = "";
      if (response.data && response.data.data) {
        uhid = response.data.data.uhid || 
               response.data.data.patient_number || 
               response.data.data.number ||
               response.data.data;
      } else if (response.data && response.data.uhid) {
        uhid = response.data.uhid;
      } else if (response.data && response.data.number) {
        uhid = response.data.number;
      } else if (typeof response.data === "string") {
        uhid = response.data;
      } else if (response.data && response.data.data && typeof response.data.data === "string") {
        uhid = response.data.data;
      }

      if (uhid) {
        setGeneratedUHID(uhid);
        setForm((prev) => ({ ...prev, uhid: uhid }));
      } else {
        setUhidError("Failed to generate UHID. Please try again.");
        const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
        setGeneratedUHID(fallbackUhid);
        setForm((prev) => ({ ...prev, uhid: fallbackUhid }));
      }
    } catch (error) {
      console.error("Error generating UHID:", error);
      
      let errorMsg = "Failed to generate UHID. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      
      setUhidError(errorMsg);
      const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
      setGeneratedUHID(fallbackUhid);
      setForm((prev) => ({ ...prev, uhid: fallbackUhid }));
    } finally {
      setIsGeneratingUHID(false);
    }
  };

  // Handle all types of inputs (text, select, checkbox)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleReset = () => {
    setForm({
      firstName: "",
      middleName: "",
      lastName: "",
      uhid: generatedUHID || "",
      dob: "",
      age: "",
      gender: "",
      bloodGroup: "",
      maritalStatus: "",
      nationality: "",
      religion: "",
      occupation: "",
      idType: "",
      idNumber: "",
      registrationDate: "",
      referredBy: "",
      address1: "",
      address2: "",
      country: "",
      state: "",
      city: "",
      postalCode: "",
      department: "",
      doctor: "",
      consultationType: "",
      primaryPhone: "",
      alternativePhone: "",
      email: "",
      whatsappNumber: "",
      preferredContactMethod: "",
      preferredContactTime: "",
      preferredLanguage: "",
      smsNotifications: false,
      emailNotifications: false,
      appointmentReminders: false,
      sameAsResidential: false,
      contactAddress: "",
      genotype: "",
      height: "",
      heightUnit: "cm",
      weight: "",
      weightUnit: "kg",
      noKnownConditions: false,
      noKnownAllergies: false,
      allergen: "",
      reaction: "",
      takingMedication: "No",
      medicationName: "",
      dosage: "",
      frequency: "",
      route: "",
      medicationStartDate: "",
      previousSurgery: "No",
      procedureName: "",
      surgeryDate: "",
      hospital: "",
      surgeon: "",
      surgeryNotes: "",
      pastMedicalHistory: "",
      familyMedicalHistory: "",
      additionalMedicalNotes: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      insuranceId: "",
    });
    setSubmitError("");
    setSubmitSuccess("");
    generateUHID();
  };

  // Handle Create Patient
  const handleCreatePatient = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    // Validate required fields
    if (!form.firstName || !form.lastName || !form.dob || !form.gender) {
      setSubmitError("Please fill in all required fields (First Name, Last Name, Date of Birth, Gender)");
      setIsSubmitting(false);
      return;
    }

    try {
      const companyId = localStorage.getItem("companyId") || "1";
      
      // Prepare the payload
      const payload = {
        company_id: parseInt(companyId),
        patient_no: form.uhid || generatedUHID,
        first_name: form.firstName,
        middle_name: form.middleName || "",
        last_name: form.lastName,
        gender: form.gender || "Male",
        date_of_birth: form.dob,
        phone: form.primaryPhone || "",
        email: form.email || "",
        address: form.address1 || "",
        blood_group: form.bloodGroup || "",
        genotype: form.genotype || "",
        marital_status: form.maritalStatus || "Single",
        occupation: form.occupation || "",
        emergency_contact_name: form.emergencyContactName || "",
        emergency_contact_relationship: form.emergencyContactRelationship || "",
        emergency_contact_phone: form.emergencyContactPhone || "",
        insurance_id: parseInt(form.insuranceId) || 0,
        status: "Active",
      };

      console.log("Creating Patient with payload:", payload);

      const response = await axios.post(
        `${BASE_URL}/api/patient/create`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("Patient Created:", response.data);

      setSubmitSuccess("Patient created successfully!");
      
      // Generate new UHID for next patient
      generateUHID();
      
      // Reset form after successful creation
      setTimeout(() => {
        handleReset();
        // Navigate to patient list after 2 seconds
        setTimeout(() => {
          navigate("/patient");
        }, 1000);
      }, 1000);

    } catch (error) {
      console.error("Error creating patient:", error);
      
      let errorMsg = "Failed to create patient. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.data?.data) {
          const apiErrors = error.response.data.data;
          if (typeof apiErrors === "object") {
            const firstKey = Object.keys(apiErrors)[0];
            if (firstKey && Array.isArray(apiErrors[firstKey])) {
              errorMsg = apiErrors[firstKey][0];
            }
          }
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { key: "personal", label: "Personal Information", icon: <FaUser /> },
    { key: "contact", label: "Contact Information", icon: <FaPhone /> },
    { key: "medical", label: "Medical Information", icon: <FaHeartbeat /> },
    { key: "emergency", label: "Emergency Contact", icon: <FaUsers /> },
    { key: "insurance", label: "Insurance Information", icon: <FaShieldAlt /> },
    { key: "documents", label: "Documents", icon: <FaFileAlt /> },
  ];

  return (
    <div className={classes.pageContainer}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={classes.mainArea}>
        <Header toggleSidebar={toggleSidebar} />

        <div className={classes.content}>
          {/* --- Top Header --- */}
          <div className={classes.topHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerIcon}>
                <FaUserPlus />
              </div>
              <div>
                <h2>Add New Patient</h2>
                <p>Fill in the details below to register a new patient</p>
              </div>
            </div>
            <button
              className={classes.closeBtn}
              onClick={() => navigate("/patient")}
            >
              <FaTimes />
            </button>
          </div>

          {/* --- Tabs --- */}
          <div className={classes.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`${classes.tab} ${activeTab === tab.key ? classes.activeTab : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* --- Body --- */}
          <div className={classes.body}>
            
            {/* Submit Success/Error Messages */}
            {submitSuccess && (
              <div className={classes.successMessage}>
                <span>{submitSuccess}</span>
              </div>
            )}
            {submitError && (
              <div className={classes.submitError}>
                <span>{submitError}</span>
              </div>
            )}

            {/* Render based on activeTab */}
            {activeTab === "personal" && (
              <>
                {/* PERSONAL DETAILS */}
                <h3>Personal Details</h3>

                <div className={classes.grid4}>
                  <div>
                    <label>First Name *</label>
                    <input name="firstName" value={form.firstName} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Middle Name</label>
                    <input name="middleName" value={form.middleName} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Last Name *</label>
                    <input name="lastName" value={form.lastName} onChange={handleChange} />
                  </div>
                  <div>
                    <label>UHID / MRN *</label>
                    <div className={classes.uhidWrapper}>
                      <input 
                        placeholder={isGeneratingUHID ? "Generating..." : "Auto generated"} 
                        value={form.uhid || generatedUHID || ""}
                        readOnly
                        className={classes.uhidInput}
                      />
                      <button 
                        type="button" 
                        className={classes.regenerateBtn}
                        onClick={generateUHID}
                        disabled={isGeneratingUHID}
                        title="Regenerate UHID"
                      >
                        {isGeneratingUHID ? "⏳" : "⟳"}
                      </button>
                    </div>
                    {uhidError && <span className={classes.uhidError}>{uhidError}</span>}
                  </div>
                  <div>
                    <label>Date of Birth *</label>
                    <input type="date" name="dob" value={form.dob} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Age *</label>
                    <input name="age" value={form.age} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Gender *</label>
                    <select name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">Select gender</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label>Blood Group</label>
                    <select name="bloodGroup" value={form.bloodGroup} onChange={handleChange}>
                      <option value="">Select blood group</option>
                      <option>A+</option>
                      <option>A-</option>
                      <option>B+</option>
                      <option>B-</option>
                      <option>AB+</option>
                      <option>AB-</option>
                      <option>O+</option>
                      <option>O-</option>
                    </select>
                  </div>
                  <div>
                    <label>Marital Status</label>
                    <select name="maritalStatus" value={form.maritalStatus} onChange={handleChange}>
                      <option value="">Select marital status</option>
                      <option>Single</option>
                      <option>Married</option>
                      <option>Divorced</option>
                      <option>Widowed</option>
                    </select>
                  </div>
                  <div>
                    <label>Nationality</label>
                    <input name="nationality" value={form.nationality} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Religion</label>
                    <input name="religion" value={form.religion} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Occupation</label>
                    <input name="occupation" value={form.occupation} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Identification Type</label>
                    <select name="idType" value={form.idType} onChange={handleChange}>
                      <option value="">Select ID Type</option>
                      <option>NIN</option>
                      <option>Passport</option>
                      <option>Driver's License</option>
                      <option>Voter's Card</option>
                    </select>
                  </div>
                  <div>
                    <label>Identification Number</label>
                    <input name="idNumber" value={form.idNumber} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Date of Registration *</label>
                    <input type="date" name="registrationDate" value={form.registrationDate} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Referred By</label>
                    <input name="referredBy" value={form.referredBy} onChange={handleChange} />
                  </div>
                </div>

                {/* ADDRESS */}
                <h3>Address Information</h3>

                <div className={classes.grid4}>
                  <div className={classes.full}>
                    <label>Address Line 1 *</label>
                    <input name="address1" value={form.address1} onChange={handleChange} />
                  </div>
                  <div className={classes.full}>
                    <label>Address Line 2</label>
                    <input name="address2" value={form.address2} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Country</label>
                    <input name="country" value={form.country} onChange={handleChange} />
                  </div>
                  <div>
                    <label>State</label>
                    <input name="state" value={form.state} onChange={handleChange} />
                  </div>
                  <div>
                    <label>City</label>
                    <input name="city" value={form.city} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Postal Code</label>
                    <input name="postalCode" value={form.postalCode} onChange={handleChange} />
                  </div>
                </div>

                {/* PRIMARY PHYSICIAN */}
                <h3>Primary Physician</h3>

                <div className={classes.grid3}>
                  <div>
                    <label>Department</label>
                    <input name="department" value={form.department} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Doctor</label>
                    <input name="doctor" value={form.doctor} onChange={handleChange} />
                  </div>
                  <div>
                    <label>Consultation Type</label>
                    <input name="consultationType" value={form.consultationType} onChange={handleChange} />
                  </div>
                </div>

                {/* Footer for Personal Info */}
                <div className={classes.footer}>
                  <div className={classes.note}>
                    After saving, you can add more details, documents and medical history from the patient profile.
                  </div>
                  <div className={classes.footerButtons}>
                    <button className={classes.reset} onClick={handleReset} type="button" disabled={isSubmitting}>
                      <FaUndo /> Reset
                    </button>
                    <button className={classes.next} onClick={() => setActiveTab("contact")}>
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* CONTACT INFORMATION TAB */}
            {activeTab === "contact" && (
              <ContactInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => setActiveTab("personal")}
                onNext={() => setActiveTab("medical")}
              />
            )}

            {/* MEDICAL INFORMATION TAB */}
            {activeTab === "medical" && (
              <MedicalInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => setActiveTab("contact")}
                onNext={() => setActiveTab("emergency")}
              />
            )}

            {/* EMERGENCY CONTACT TAB */}
            {activeTab === "emergency" && (
              <div>
                <h3>Emergency Contact Information</h3>
                <div className={classes.grid3}>
                  <div>
                    <label>Emergency Contact Name</label>
                    <input 
                      name="emergencyContactName" 
                      value={form.emergencyContactName} 
                      onChange={handleChange} 
                      placeholder="Enter emergency contact name"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label>Relationship</label>
                    <input 
                      name="emergencyContactRelationship" 
                      value={form.emergencyContactRelationship} 
                      onChange={handleChange} 
                      placeholder="e.g., Spouse, Parent, Sibling"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label>Emergency Contact Phone</label>
                    <input 
                      name="emergencyContactPhone" 
                      value={form.emergencyContactPhone} 
                      onChange={handleChange} 
                      placeholder="Enter phone number"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Please provide emergency contact information for the patient.
                  </div>
                  <div className={classes.footerButtons}>
                    <button className={classes.reset} onClick={() => setActiveTab("medical")} disabled={isSubmitting}>
                      <FaArrowLeft /> Previous
                    </button>
                    <button className={classes.next} onClick={() => setActiveTab("insurance")}>
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* INSURANCE TAB */}
            {activeTab === "insurance" && (
              <div>
                <h3>Insurance Information</h3>
                <div className={classes.grid3}>
                  <div>
                    <label>Insurance ID</label>
                    <input 
                      name="insuranceId" 
                      value={form.insuranceId} 
                      onChange={handleChange} 
                      placeholder="Enter insurance ID"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Please provide insurance information if applicable.
                  </div>
                  <div className={classes.footerButtons}>
                    <button className={classes.reset} onClick={() => setActiveTab("emergency")} disabled={isSubmitting}>
                      <FaArrowLeft /> Previous
                    </button>
                    <button 
                      className={classes.createBtn} 
                      onClick={handleCreatePatient} 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Creating..."
                      ) : (
                        <>
                          <FaSave /> Create Patient
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientPage;